import type Widget from "@/model/widget";
import React from "react";
import WidgetCard from "./widget-card";
import { Button } from "@/components/ui/button";
import { CircleArrowRight } from "lucide-react";
import useUserInfo from "@/hooks/use-user-info";
import { useNavigate } from "react-router-dom";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { formatDate } from "@/lib/date-utils";
import { MEMBER_STATUS, QueryKeys } from "@/constants";
import { getGroupIdLabel } from "@/lib/client-utils";
import { capitalize } from "@/lib/utils";
import type { GlobalFlag } from "@/model/global-flag";
import { queryClient } from "@/hooks/use-query-client";

type BenefitDetailWidgetProps = {
  className?: string;
  widget: Widget;
};

const BenefitDetailWidget: React.FunctionComponent<
  BenefitDetailWidgetProps
> = ({ widget }) => {
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const showMyBenefits = (
    queryClient.getQueryData([QueryKeys.GLOBAL_FLAGS, userInfo.username])
      ?.data?.[0] as GlobalFlag
  )?.showMyBenefits;

  const viewAllPlanBenefit = () => {
    const targetPath = `/${ScreenRouteNames.MY_BENEFIT.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewAllPlanBenefit}
    >
      View All Plan Benefits <CircleArrowRight className="ml-1" />
    </Button>
  );

  const fullName = [userInfo?.firstname, userInfo?.lastname]
    .filter(Boolean)
    .join(" ");

  const displayName = fullName || "N/A";

  const status = activeCoverage?.status?.toLowerCase();
  const isActive = status === MEMBER_STATUS?.ACTIVE?.toLowerCase();

  const statusClasses = isActive
    ? "text-white bg-green-600"
    : "text-black bg-yellow-300";

  return (
    <WidgetCard
      title={widget?.widgetDisplayName}
      footerContent={showMyBenefits ? footerContent : null}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col">
          <h6 className="mb-2 font-bold">MEMBER DETAILS</h6>
          <p className="mb-1 text-sm text-gray-500">
            <b>Member Name:</b> {displayName}
            {activeCoverage?.status && (
              <span
                className={`text ml-3 rounded-[33px] px-3 py-1 ${statusClasses}`}
              >
                {capitalize(activeCoverage?.status)}
              </span>
            )}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>Member ID:</b> {userInfo?.plainEmployeeId ?? "N/A"}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>Group Name:</b> {userInfo?.divisionName ?? "N/A"}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>{`${getGroupIdLabel(userInfo?.groupId)}:`}</b>{" "}
            {userInfo?.divisionId ?? "N/A"}
          </p>
          {activeCoverage?.mbrEffDate ? (
            <p className="mb-1 text-sm text-gray-500">
              <b>Effective Date:</b>{" "}
              {formatDate(activeCoverage?.mbrEffDate) ?? "N/A"}
            </p>
          ) : null}
          {activeCoverage?.mbrTermDate && !isActive ? (
            <p className="mb-1 text-sm text-gray-500">
              <b>Termination Date:</b>{" "}
              {formatDate(activeCoverage?.mbrTermDate) ?? "N/A"}
            </p>
          ) : null}
        </div>
        <div className="">
          <h6 className="mb-2 font-bold">PLAN DETAILS</h6>
          <p className="mb-1 text-sm text-gray-500">
            <b>Plan Name:</b> {activeCoverage?.planDesc ?? "N/A"}
          </p>

          <p className="mb-1 text-sm text-gray-500">
            <b>Plan Year Start Date:</b>{" "}
            {activeCoverage?.planStartDate
              ? formatDate(activeCoverage?.planStartDate)
              : "N/A"}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>Plan Year End Date:</b>{" "}
            {activeCoverage?.planEndDate
              ? formatDate(activeCoverage?.planEndDate)
              : "N/A"}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>Coverage Type:</b>{" "}
            {activeCoverage?.claimCoverageCodeDesc ?? "N/A"}
          </p>
          <p className="mb-1 text-sm text-gray-500">
            <b>Coverage Tier:</b> {activeCoverage?.insCoverageTypeDesc ?? "N/A"}
          </p>
          {/* <p className="mb-1 text-sm text-gray-500">
            <b>Status:</b> {activeCoverage?.status ?? "N/A"}
          </p> */}
        </div>
      </div>
    </WidgetCard>
  );
};

export default BenefitDetailWidget;
